import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bookmark, Copy, CheckCircle2, ShoppingCart, Loader2, Sparkles, ChevronDown, SearchCheck, Smartphone } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { isNative, runNativeTrolley } from "../lib/nativeTrolley";

const isMobile = () => /iPhone|iPad|Android/i.test(navigator.userAgent);

export default function FillTrolley({ activeId, shop, label, onDone }) {
  const navigate = useNavigate();
  const native = isNative();
  const [, setSetup] = useState(null);
  const [installed, setInstalled] = useState(() => localStorage.getItem("sorted_helper_installed") === "1");
  const [session, setSession] = useState(null);
  const [starting, setStarting] = useState(false);
  const [status, setStatus] = useState(null);
  const [connectedAt, setConnectedAt] = useState(null);
  const timer = useRef(null);
  const [mode, setMode] = useState("best");
  const MODES = [["best", "Best match"], ["cheapest", "Cheapest"], ["house", "House brand"]];
  const setPickMode = async (m) => {
    setMode(m);
    try { await api.put("/trolley/pick-mode", { mode: m }); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  useEffect(() => { api.get("/trolley/setup").then(({ data }) => { setSetup(data); setMode(data.pick_mode || "best"); setConnectedAt(data.helper_connected_at || null); }).catch(() => {}); }, []);
  useEffect(() => () => clearInterval(timer.current), []);

  const poll = (token) => {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/trolley/status/${token}`);
        setStatus(data);
        if (data.helper_connected_at) { setConnectedAt(data.helper_connected_at); if (!installed) markInstalled(); }
        if (data.status === "done") { clearInterval(timer.current); onDone?.(); toast.success(`${data.result?.added?.length || 0} items landed in your ${label} trolley`); }
        if (data.status === "matched") { clearInterval(timer.current); onDone?.(); toast.success(`${data.result?.added?.length || 0} products matched at ${label}`); }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const start = async (runMode = "add") => {
    setStarting(true);
    try {
      const { data } = await api.post("/trolley/start", { household_id: activeId, shop, mode: runMode });
      setSession({ ...data, mode: runMode }); setStatus({ status: "waiting" });
      if (native) await runNativeTrolley({ openUrl: data.open_url, helperKey: data.helper_key, onClosed: () => {} });
      else window.open(data.open_url, "_blank", "noopener,noreferrer");
      poll(data.token);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setStarting(false); }
  };

  const markInstalled = () => { localStorage.setItem("sorted_helper_installed", "1"); setInstalled(true); };

  const added = status?.result?.added?.length || 0;
  const failed = status?.result?.failed || [];

  return (
    <div className="mt-4 bg-white rounded-3xl border-2 border-[#FDECEF] p-4 shadow-soft" data-testid="fill-trolley">
      <div className="flex items-center gap-2">
        <span className="w-9 h-9 rounded-2xl bg-[#DDEBE1] flex items-center justify-center text-emerald-600"><ShoppingCart size={17} strokeWidth={2.5} /></span>
        <div className="flex-1">
          <p className="font-heading font-semibold text-sorted-ink leading-tight">Fill my {label} trolley</p>
          {connectedAt && <p className="text-[11px] font-bold text-emerald-700 flex items-center gap-1" data-testid="helper-connected"><CheckCircle2 size={13} strokeWidth={2.5} /> Millie bookmark connected · last used {new Date(connectedAt).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</p>}
          <p className="text-[11px] font-semibold text-sorted-slate">{shop === "woolies" ? "Millie matches every item to a real product and adds it while you're logged in." : "Millie matches items to Coles products — Coles needs one tap per item to add."}</p>
        </div>
      </div>

      <div className="mt-3" data-testid="pick-mode">
        <p className="text-[11px] font-bold text-sorted-slate mb-1.5">How should Millie pick products?</p>
        <div className="flex gap-1.5">
          {MODES.map(([k, l]) => (
            <button key={k} onClick={() => setPickMode(k)} data-testid={`pick-mode-${k}`}
              className={`flex-1 py-2 rounded-full text-[11px] font-bold border-2 transition-colors ${mode === k ? "bg-sorted-ink border-sorted-ink text-white" : "bg-white border-[#FDECEF] text-sorted-slate"}`}>{l}</button>
          ))}
        </div>
        <p className="text-[11px] text-sorted-slate mt-1.5">{mode === "cheapest" ? "Lowest unit price among the matches — you can still override any item." : mode === "house" ? "Woolworths / Macro / Essentials / Coles brand first, cheapest of those." : "Closest product to what the recipe asks for."}</p>
      </div>

      {!native && !installed && (
        <button onClick={() => navigate("/helper-setup")} data-testid="helper-setup-link" className="mt-3 w-full bg-[#FFF6F1] rounded-2xl p-3 text-left flex items-center gap-2">
          <Bookmark size={15} strokeWidth={2.5} className="text-sorted-rose shrink-0" />
          <span className="text-xs font-bold text-sorted-ink flex-1">One-time setup needed — add Millie's bookmark (takes a minute). Tap for step-by-step.</span>
          <ChevronDown size={14} className="-rotate-90 text-sorted-slate" />
        </button>
      )}
      {native && <p className="mt-3 text-[11px] font-bold text-emerald-700 flex items-center gap-1" data-testid="native-ready"><Smartphone size={13} strokeWidth={2.5} /> No setup needed — Millie opens {label} inside Sorted and does it for you.</p>}

      <button onClick={() => start("add")} disabled={starting} data-testid="fill-trolley-start"
        className="mt-3 w-full bg-sorted-rose text-white font-bold py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-60">
        {starting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} strokeWidth={2.5} />} {session ? `Open ${label} again` : `Fill my ${label} trolley`}
      </button>
      <button onClick={() => start("match")} disabled={starting} data-testid="match-only-start"
        className="mt-2 w-full bg-white border-2 border-[#FDECEF] text-sorted-ink font-bold py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-60">
        <SearchCheck size={15} strokeWidth={2.5} /> Match my list only (don't add yet)
      </button>
      {!native && installed && (
        <button onClick={() => navigate("/helper-setup")} data-testid="helper-show-setup" className="mt-2 w-full text-[11px] font-bold text-sorted-slate flex items-center justify-center gap-1"><ChevronDown size={12} /> Helper setup &amp; test</button>
      )}

      {session && status && (
        <div className="mt-3 rounded-2xl bg-[#FFF6F1] p-3" data-testid="fill-trolley-status">
          {status.status === "connected" && <p className="text-xs font-bold text-emerald-700 flex items-center gap-2" data-testid="helper-connected-live"><CheckCircle2 size={14} strokeWidth={2.5} /> Millie's bookmark is connected on {label} — matching your items now.</p>}
          {status.status === "waiting" && (
            <div>
              <p className="text-xs font-semibold text-sorted-ink flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> {label} is open in a new tab — log in, then tap your <b>Millie</b> bookmark. I'll wait here.</p>
              <p className="text-[11px] text-sorted-slate font-semibold mt-2" data-testid="access-denied-fallback">Seeing <b>"Access Denied"</b>? That's {label} blocking the tab Sorted opened. No worries — open {label} yourself in Chrome or Safari, log in, then tap the <b>Millie</b> bookmark there. The helper works from any {label} tab.</p>
              <button onClick={async () => { try { await navigator.clipboard.writeText(session.open_url); toast.success(`${label} link copied`); } catch { toast.error("Couldn't copy"); } }} data-testid="copy-shop-link"
                className="mt-2 text-[11px] font-bold text-sorted-rose flex items-center gap-1"><Copy size={12} strokeWidth={2.5} /> Copy {label} link</button>
            </div>
          )}
          {status.status === "matching" && <p className="text-xs font-semibold text-sorted-ink flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Matching your {session.items.length} items to {label} products…</p>}
          {status.status === "adding" && <p className="text-xs font-semibold text-sorted-ink flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Adding to your trolley…</p>}
          {status.status === "matched" && <p className="text-sm font-bold text-sorted-ink" data-testid="matched-done">✅ {added} products matched — your list now shows the real {label} products.</p>}
          {status.status === "done" && (
            <div>
              <p className="text-sm font-bold text-sorted-ink">✅ {added} in the trolley{status.result?.total_price ? ` · ~A$${Number(status.result.total_price).toFixed(2)}` : ""}</p>
              {status.result?.savings > 0 && <p className="text-[11px] font-bold text-emerald-700 mt-0.5" data-testid="trolley-savings">Saved ~A${Number(status.result.savings).toFixed(2)} vs best match ({status.result.pick_mode === "house" ? "house brand" : "cheapest"} picks)</p>}
              {failed.length > 0 && <p className="text-[11px] font-semibold text-sorted-slate mt-1">Need a look: {failed.join(", ")}</p>}
              <a href={shop === "woolies" ? "https://www.woolworths.com.au/shop/cart" : "https://www.coles.com.au/trolley"} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs font-bold text-sorted-rose">Open {label} trolley →</a>
            </div>
          )}
          <details className="mt-2">
            <summary className="text-[11px] font-bold text-sorted-slate cursor-pointer">What Millie will look for ({session.items.length})</summary>
            <ul className="mt-1 space-y-0.5">
              {session.items.map((it) => <li key={it.name} className="text-[11px] text-sorted-slate font-semibold" data-testid={`trolley-item-${it.name}`}><b className="text-sorted-ink">{it.search_term}</b> ×{it.qty} <span className="opacity-70">for {it.name}{it.note ? ` · ${it.note}` : ""}</span></li>)}
            </ul>
          </details>
        </div>
      )}
    </div>
  );
}
